'use client';

import Icon from '@/components/ui/AppIcon';

interface LiabilityTermsSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function LiabilityTermsSection({ isExpanded, onToggle }: LiabilityTermsSectionProps) {
  const liabilityAreas = [
    {
      title: 'Property Damage Policies',
      description: 'Guests are financially responsible for any damage caused to the property, furnishings, or amenities during their stay.',
      points: [
        'Security deposit held to cover potential damages',
        'Damage assessment conducted during checkout inspection',
        'Repair/replacement costs deducted from security deposit',
        'Additional charges billed if damages exceed deposit amount',
        'Photographic evidence provided for all damage claims'
      ]
    },
    {
      title: 'Insurance Coverage',
      description: 'Proximus maintains comprehensive insurance coverage for the property and common areas.',
      points: [
        'Property insurance covers structural damages and common areas',
        'Guests advised to maintain personal travel insurance',
        'Personal belongings not covered under property insurance',
        'Third-party liability insurance for guest-caused damages',
        'Insurance claims processed within 30 business days'
      ]
    },
    {
      title: 'Limitation of Liability',
      description: 'Proximus liability is limited under specific circumstances as outlined below.',
      points: [
        'Not liable for loss or theft of personal belongings',
        'Not responsible for injuries due to guest negligence',
        'Limited liability for service interruptions beyond our control',
        'Maximum liability capped at total booking value',
        'Force majeure events exempt from liability claims'
      ]
    },
    {
      title: 'Dispute Resolution Procedures',
      description: 'Clear escalation path for resolving disputes between guests and management.',
      points: [
        'Step 1: Report issue to front desk or property manager immediately',
        'Step 2: Formal complaint submitted in writing within 24 hours',
        'Step 3: Management review and response within 48 hours',
        'Step 4: Mediation through neutral third party if unresolved',
        'Step 5: Legal arbitration under Indian jurisdiction as final resort'
      ]
    }
  ];

  return (
    <section id="liability-terms" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="ShieldExclamationIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Section 3</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              Liability Terms
            </h2>
            <p className="text-lg text-text-secondary">
              Property damage policies, insurance coverage, limitation of liability, and dispute resolution procedures.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-premium border border-border overflow-hidden">
            <button
              onClick={onToggle}
              className="w-full px-6 py-6 flex items-center justify-between hover:bg-muted transition-colors duration-300"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="ScaleIcon" size={24} className="text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-semibold text-text-primary">View Liability Terms</h3>
                  <p className="text-sm text-text-secondary mt-1">Click to expand liability policies and dispute resolution</p>
                </div>
              </div>
              <Icon 
                name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"} 
                size={24} 
                className="text-text-secondary flex-shrink-0" 
              />
            </button>

            {isExpanded && (
              <div className="px-6 pb-6 space-y-6 animate-scale-in">
                {liabilityAreas.map((area, index) => (
                  <div key={index} className="border-t border-border pt-6">
                    <h4 className="text-lg font-semibold text-text-primary mb-2">{area.title}</h4>
                    <p className="text-text-secondary mb-4">{area.description}</p>
                    <ul className="space-y-2">
                      {area.points.map((point, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-text-secondary">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="border-t border-border pt-6">
                  <div className="bg-accent/5 border border-accent/20 rounded-xl p-6">
                    <div className="flex items-start space-x-3">
                      <Icon name="InformationCircleIcon" size={24} className="text-accent flex-shrink-0" />
                      <div>
                        <h5 className="font-semibold text-text-primary mb-2">Important Notice</h5>
                        <p className="text-sm text-text-secondary">
                          All disputes are subject to the exclusive jurisdiction of courts in Bangalore, Karnataka, India. By accepting these terms, you agree to resolve any disputes through the outlined procedures before pursuing legal action.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}